import axios from 'axios'

// Reads a readable (non-httpOnly) cookie — the gateway's double-submit CSRF token (tk_csrf),
// which is domain-scoped so live.<academy> can read it.
function readCookie (name) {
    if (typeof document === 'undefined') return ''
    const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')
    return match ? match.pop() : ''
}

// Is the configured API base ABSOLUTE (a different origin) rather than a relative path?
// Deployed builds set VUE_APP_API_URL=/api — same-origin via the ingress path-split. Local dev
// has no path-split, so it points at the gateway host directly and IS cross-origin.
// Exported for tests: it decides whether the CSRF machinery is needed at all.
export function isCrossOriginBase (base) {
    return /^https?:\/\//i.test(base || '')
}

// Add the double-submit header to a state-changing request. Pure so it can be tested without
// reaching into axios internals. Safe methods are exempt (the gateway's csrfGuard ignores them).
export function attachCsrfHeader (config, readToken = () => readCookie('tk_csrf')) {
    const method = (config.method || 'get').toUpperCase()

    if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') return config

    const token = readToken()

    if (token) {
        config.headers = { ...config.headers, 'X-CSRF-Token': token }
    }

    return config
}

// The live-course client's HTTP transport. The session rides the shared httpOnly cookie
// (withCredentials) and the gateway injects the Knox token server-side — no token in JS.
//
// SAME-ORIGIN in deployed environments (uniform same-origin convergence, Obsidian note 20 §8):
// VUE_APP_API_URL is the relative "/api", which the ingress path-splits to the gateway, so the
// browser makes no cross-origin request and needs no CSRF header — csrfGuard clears a same-origin
// request on the Origin check alone. LOCAL DEV keeps the cross-origin path (no dev path-split),
// and there the double-submit header IS required, so the interceptor is mounted only in that case.
const http = axios.create({
    withCredentials: true
})

if (isCrossOriginBase(process.env.VUE_APP_API_URL)) {
    http.interceptors.request.use((config) => attachCsrfHeader(config))
}

export default http
